/**
 * CSG Helper for THREE.js
 * Provides boolean operations (union, subtract, intersect) on meshes
 * Based on CSG.js algorithm adapted for THREE.js BufferGeometry
 */

// CSG.js - Constructive Solid Geometry
// Adapted from https://github.com/evanw/csg.js/
(function() {
  'use strict';

  // CSG Vertex
  class CSGVertex {
    constructor(pos, normal, uv) {
      this.pos = pos.clone();
      this.normal = normal.clone();
      this.uv = uv ? uv.clone() : new THREE.Vector2();
    }

    clone() {
      return new CSGVertex(this.pos, this.normal, this.uv);
    }

    flip() {
      this.normal.negate();
    }

    interpolate(other, t) {
      return new CSGVertex(
        this.pos.clone().lerp(other.pos, t),
        this.normal.clone().lerp(other.normal, t),
        this.uv.clone().lerp(other.uv, t)
      );
    }
  }

  // CSG Plane
  const EPSILON = 1e-5;
  const COPLANAR = 0;
  const FRONT = 1;
  const BACK = 2;
  const SPANNING = 3;

  class CSGPlane {
    constructor(normal, w) {
      this.normal = normal;
      this.w = w;
    }

    clone() {
      return new CSGPlane(this.normal.clone(), this.w);
    }

    flip() {
      this.normal.negate();
      this.w = -this.w;
    }

    splitPolygon(polygon, coplanarFront, coplanarBack, front, back) {
      let polygonType = 0;
      const types = [];

      for (let i = 0; i < polygon.vertices.length; i++) {
        const t = this.normal.dot(polygon.vertices[i].pos) - this.w;
        const type = (t < -EPSILON) ? BACK : (t > EPSILON) ? FRONT : COPLANAR;
        polygonType |= type;
        types.push(type);
      }

      switch (polygonType) {
        case COPLANAR:
          (this.normal.dot(polygon.plane.normal) > 0 ? coplanarFront : coplanarBack).push(polygon);
          break;
        case FRONT:
          front.push(polygon);
          break;
        case BACK:
          back.push(polygon);
          break;
        case SPANNING:
          const f = [], b = [];
          for (let i = 0; i < polygon.vertices.length; i++) {
            const j = (i + 1) % polygon.vertices.length;
            const ti = types[i], tj = types[j];
            const vi = polygon.vertices[i], vj = polygon.vertices[j];
            if (ti !== BACK) f.push(vi);
            if (ti !== FRONT) b.push(ti !== BACK ? vi.clone() : vi);
            if ((ti | tj) === SPANNING) {
              const t = (this.w - this.normal.dot(vi.pos)) / this.normal.dot(vj.pos.clone().sub(vi.pos));
              const v = vi.interpolate(vj, t);
              f.push(v);
              b.push(v.clone());
            }
          }
          if (f.length >= 3) front.push(new CSGPolygon(f, polygon.shared));
          if (b.length >= 3) back.push(new CSGPolygon(b, polygon.shared));
          break;
      }
    }

    static fromPoints(a, b, c) {
      const n = b.clone().sub(a).cross(c.clone().sub(a)).normalize();
      return new CSGPlane(n, n.dot(a));
    }
  }

  // CSG Polygon
  class CSGPolygon {
    constructor(vertices, shared) {
      this.vertices = vertices;
      this.shared = shared;
      this.plane = CSGPlane.fromPoints(vertices[0].pos, vertices[1].pos, vertices[2].pos);
    }

    clone() {
      const vertices = this.vertices.map(v => v.clone());
      return new CSGPolygon(vertices, this.shared);
    }

    flip() {
      this.vertices.reverse().forEach(v => v.flip());
      this.plane.flip();
    }
  }

  // CSG Node (BSP Tree)
  class CSGNode {
    constructor(polygons) {
      this.plane = null;
      this.front = null;
      this.back = null;
      this.polygons = [];
      if (polygons) this.build(polygons);
    }

    clone() {
      const node = new CSGNode();
      node.plane = this.plane && this.plane.clone();
      node.front = this.front && this.front.clone();
      node.back = this.back && this.back.clone();
      node.polygons = this.polygons.map(p => p.clone());
      return node;
    }

    invert() {
      for (let i = 0; i < this.polygons.length; i++) {
        this.polygons[i].flip();
      }
      if (this.plane) this.plane.flip();
      if (this.front) this.front.invert();
      if (this.back) this.back.invert();
      const temp = this.front;
      this.front = this.back;
      this.back = temp;
    }

    clipPolygons(polygons) {
      if (!this.plane) return polygons.slice();
      let front = [], back = [];
      for (let i = 0; i < polygons.length; i++) {
        this.plane.splitPolygon(polygons[i], front, back, front, back);
      }
      if (this.front) front = this.front.clipPolygons(front);
      if (this.back) back = this.back.clipPolygons(back);
      else back = [];
      return front.concat(back);
    }

    clipTo(bsp) {
      this.polygons = bsp.clipPolygons(this.polygons);
      if (this.front) this.front.clipTo(bsp);
      if (this.back) this.back.clipTo(bsp);
    }

    allPolygons() {
      let polygons = this.polygons.slice();
      if (this.front) polygons = polygons.concat(this.front.allPolygons());
      if (this.back) polygons = polygons.concat(this.back.allPolygons());
      return polygons;
    }

    build(polygons) {
      if (!polygons.length) return;
      if (!this.plane) this.plane = polygons[0].plane.clone();
      const front = [], back = [];
      for (let i = 0; i < polygons.length; i++) {
        this.plane.splitPolygon(polygons[i], this.polygons, this.polygons, front, back);
      }
      if (front.length) {
        if (!this.front) this.front = new CSGNode();
        this.front.build(front);
      }
      if (back.length) {
        if (!this.back) this.back = new CSGNode();
        this.back.build(back);
      }
    }
  }

  // Main CSG class
  class CSG {
    constructor() {
      this.polygons = [];
    }

    clone() {
      const csg = new CSG();
      csg.polygons = this.polygons.map(p => p.clone());
      return csg;
    }

    toPolygons() {
      return this.polygons;
    }

    union(csg) {
      const a = new CSGNode(this.clone().polygons);
      const b = new CSGNode(csg.clone().polygons);
      a.clipTo(b);
      b.clipTo(a);
      b.invert();
      b.clipTo(a);
      b.invert();
      a.build(b.allPolygons());
      return CSG.fromPolygons(a.allPolygons());
    }

    subtract(csg) {
      const a = new CSGNode(this.clone().polygons);
      const b = new CSGNode(csg.clone().polygons);
      a.invert();
      a.clipTo(b);
      b.clipTo(a);
      b.invert();
      b.clipTo(a);
      b.invert();
      a.build(b.allPolygons());
      a.invert();
      return CSG.fromPolygons(a.allPolygons());
    }

    intersect(csg) {
      const a = new CSGNode(this.clone().polygons);
      const b = new CSGNode(csg.clone().polygons);
      a.invert();
      b.clipTo(a);
      b.invert();
      a.clipTo(b);
      b.clipTo(a);
      a.build(b.allPolygons());
      a.invert();
      return CSG.fromPolygons(a.allPolygons());
    }

    inverse() {
      const csg = this.clone();
      csg.polygons.forEach(p => p.flip());
      return csg;
    }

    static fromPolygons(polygons) {
      const csg = new CSG();
      csg.polygons = polygons;
      return csg;
    }

    // Convert THREE.js BufferGeometry to CSG
    static fromGeometry(geometry, matrix) {
      const polygons = [];
      const posAttr = geometry.getAttribute('position');
      const normAttr = geometry.getAttribute('normal');
      const uvAttr = geometry.getAttribute('uv');
      const index = geometry.getIndex();

      const getVertex = (i) => {
        const pos = new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
        const norm = normAttr ? new THREE.Vector3(normAttr.getX(i), normAttr.getY(i), normAttr.getZ(i)) : new THREE.Vector3(0, 1, 0);
        const uv = uvAttr ? new THREE.Vector2(uvAttr.getX(i), uvAttr.getY(i)) : new THREE.Vector2();

        if (matrix) {
          pos.applyMatrix4(matrix);
          norm.applyMatrix3(new THREE.Matrix3().getNormalMatrix(matrix)).normalize();
        }

        return new CSGVertex(pos, norm, uv);
      };

      if (index) {
        for (let i = 0; i < index.count; i += 3) {
          const a = index.getX(i);
          const b = index.getX(i + 1);
          const c = index.getX(i + 2);
          const vertices = [getVertex(a), getVertex(b), getVertex(c)];
          polygons.push(new CSGPolygon(vertices, null));
        }
      } else {
        for (let i = 0; i < posAttr.count; i += 3) {
          const vertices = [getVertex(i), getVertex(i + 1), getVertex(i + 2)];
          polygons.push(new CSGPolygon(vertices, null));
        }
      }

      return CSG.fromPolygons(polygons);
    }

    // Convert THREE.js Mesh to CSG
    static fromMesh(mesh) {
      const geometry = mesh.geometry.clone();
      if (!geometry.isBufferGeometry) {
        console.error('CSG requires BufferGeometry');
        return new CSG();
      }
      mesh.updateMatrixWorld();
      return CSG.fromGeometry(geometry, mesh.matrixWorld);
    }

    // Convert CSG to THREE.js BufferGeometry
    toGeometry() {
      const positions = [];
      const normals = [];
      const uvs = [];

      this.polygons.forEach(polygon => {
        // Triangulate polygon (assuming convex)
        for (let i = 2; i < polygon.vertices.length; i++) {
          const v0 = polygon.vertices[0];
          const v1 = polygon.vertices[i - 1];
          const v2 = polygon.vertices[i];

          positions.push(v0.pos.x, v0.pos.y, v0.pos.z);
          positions.push(v1.pos.x, v1.pos.y, v1.pos.z);
          positions.push(v2.pos.x, v2.pos.y, v2.pos.z);

          normals.push(v0.normal.x, v0.normal.y, v0.normal.z);
          normals.push(v1.normal.x, v1.normal.y, v1.normal.z);
          normals.push(v2.normal.x, v2.normal.y, v2.normal.z);

          uvs.push(v0.uv.x, v0.uv.y);
          uvs.push(v1.uv.x, v1.uv.y);
          uvs.push(v2.uv.x, v2.uv.y);
        }
      });

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));

      return geometry;
    }

    // Convert CSG to THREE.js Mesh
    toMesh(material) {
      const geometry = this.toGeometry();
      return new THREE.Mesh(geometry, material);
    }
  }

  // Expose CSG globally
  window.CSG = CSG;
})();


/**
 * CSGCutter - High-level API for cutting meshes with planes and boxes
 */
class CSGCutter {
  constructor() {
    this.originalMesh = null;
    this.resultMesh = null;
    this.cuts = []; // Array of cut definitions
  }

  /**
   * Set the mesh to be cut
   */
  setMesh(mesh) {
    this.originalMesh = mesh;
    this.resultMesh = null;
  }

  /**
   * Add a plane cut (infinite in one direction, bounded by U/V)
   * @param {Object} params - { origin, normal, width, height, depth }
   */
  addPlaneCut(params) {
    this.cuts.push({
      type: 'plane',
      ...params
    });
  }

  /**
   * Add a box cut
   * @param {Object} params - { origin, normal, width, height, depth }
   */
  addBoxCut(params) {
    this.cuts.push({
      type: 'box',
      ...params
    });
  }

  /**
   * Clear all cuts
   */
  clearCuts() {
    this.cuts = [];
    this.resultMesh = null;
  }

  /**
   * Create a box geometry for a cut
   */
  createCutBoxGeometry(cut) {
    const { origin, normal, width, height, depth } = cut;

    // Create local coordinate system
    const normalVec = new THREE.Vector3(normal[0], normal[1], normal[2]).normalize();
    const worldUp = Math.abs(normalVec.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const uAxis = new THREE.Vector3().crossVectors(worldUp, normalVec).normalize();
    const vAxis = new THREE.Vector3().crossVectors(normalVec, uAxis).normalize();

    // For plane cuts (depth = 0), create a very large box extending in -normal direction
    const actualDepth = depth > 0.001 ? depth : 1000; // Large depth for planes

    // Create box geometry
    const boxGeom = new THREE.BoxGeometry(width, height, actualDepth);

    // Position the box so its front face is at origin
    // For depth > 0: box is centered at origin - depth/2 * normal
    // For planes: box extends far in -normal direction
    const originVec = new THREE.Vector3(origin[0], origin[1], origin[2]);
    const boxCenter = originVec.clone().sub(normalVec.clone().multiplyScalar(actualDepth / 2));

    // Create rotation matrix to align box with plane orientation
    const rotMatrix = new THREE.Matrix4();
    rotMatrix.makeBasis(uAxis, vAxis, normalVec);

    // Create transform matrix
    const transform = new THREE.Matrix4();
    transform.makeTranslation(boxCenter.x, boxCenter.y, boxCenter.z);
    transform.multiply(rotMatrix);

    // Apply transform to geometry
    boxGeom.applyMatrix4(transform);

    return boxGeom;
  }

  /**
   * Apply all cuts to the mesh using CSG operations
   * @returns {THREE.Mesh} The resulting cut mesh
   */
  applyCuts() {
    if (!this.originalMesh || this.cuts.length === 0) {
      return this.originalMesh;
    }

    console.log(`[CSGCutter] Applying ${this.cuts.length} cuts...`);
    const startTime = performance.now();

    try {
      // Convert original mesh to CSG
      let resultCSG = CSG.fromMesh(this.originalMesh);

      // Create union of all cut shapes
      let cutUnionCSG = null;

      for (let i = 0; i < this.cuts.length; i++) {
        const cut = this.cuts[i];
        const cutGeom = this.createCutBoxGeometry(cut);
        const cutMesh = new THREE.Mesh(cutGeom);
        const cutCSG = CSG.fromMesh(cutMesh);

        if (cutUnionCSG === null) {
          cutUnionCSG = cutCSG;
        } else {
          cutUnionCSG = cutUnionCSG.union(cutCSG);
        }

        console.log(`[CSGCutter] Processed cut ${i + 1}/${this.cuts.length}`);
      }

      // Subtract the union of cuts from the original mesh
      resultCSG = resultCSG.subtract(cutUnionCSG);

      // Convert back to THREE.js mesh
      const resultGeom = resultCSG.toGeometry();
      const resultMesh = new THREE.Mesh(resultGeom, this.originalMesh.material.clone());

      const endTime = performance.now();
      console.log(`[CSGCutter] CSG operations completed in ${(endTime - startTime).toFixed(0)}ms`);

      this.resultMesh = resultMesh;
      return resultMesh;

    } catch (error) {
      console.error('[CSGCutter] CSG operation failed:', error);
      return this.originalMesh;
    }
  }

  /**
   * Get the result mesh (or original if no cuts applied)
   */
  getResult() {
    return this.resultMesh || this.originalMesh;
  }
}

// Expose CSGCutter globally
window.CSGCutter = CSGCutter;
